
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export interface LogContext {
  component?: string;
  action?: string;
  data?: any;
  timestamp?: string;
  [key: string]: any; // Allow additional properties
}

class LoggingService {
  private static instance: LoggingService;
  private logLevel: LogLevel;
  private isDevelopment: boolean;

  private constructor() {
    this.isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development';
    // In production, only show ERROR and WARN logs
    // In development, show all logs
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const component = context?.component ? `[${context.component}]` : '';
    const action = context?.action ? `(${context.action})` : '';
    
    return `${timestamp} ${level} ${component}${action}: ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.logLevel;
  }

  // Critical errors - always logged in production and development
  error(message: string, context?: LogContext, error?: Error): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const formattedMessage = this.formatMessage('ERROR', message, context);
      console.error(formattedMessage, context?.data, error);
    }
  }

  // Warnings - logged in production and development
  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const formattedMessage = this.formatMessage('WARN', message, context);
      console.warn(formattedMessage, context?.data);
    }
  }

  // Info logs - only in development (reduces production noise)
  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const formattedMessage = this.formatMessage('INFO', message, context);
      console.log(formattedMessage, context?.data);
    }
  }

  // Debug logs - only in development
  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const formattedMessage = this.formatMessage('DEBUG', message, context);
      console.log(formattedMessage, context?.data);
    }
  }

  // Service-specific logging methods for better organization
  studentService = {
    error: (message: string, data?: any, error?: Error) => 
      this.error(message, { component: 'StudentService', data }, error),
    warn: (message: string, data?: any) => 
      this.warn(message, { component: 'StudentService', data }),
    info: (message: string, data?: any) => 
      this.info(message, { component: 'StudentService', data }),
    debug: (message: string, data?: any) => 
      this.debug(message, { component: 'StudentService', data })
  };

  teacherService = {
    error: (message: string, data?: any, error?: Error) => 
      this.error(message, { component: 'TeacherService', data }, error),
    warn: (message: string, data?: any) => 
      this.warn(message, { component: 'TeacherService', data }),
    info: (message: string, data?: any) => 
      this.info(message, { component: 'TeacherService', data }),
    debug: (message: string, data?: any) => 
      this.debug(message, { component: 'TeacherService', data })
  };

  storageService = {
    error: (message: string, data?: any, error?: Error) => 
      this.error(message, { component: 'StorageService', data }, error),
    warn: (message: string, data?: any) => 
      this.warn(message, { component: 'StorageService', data }),
    info: (message: string, data?: any) => 
      this.info(message, { component: 'StorageService', data }),
    debug: (message: string, data?: any) => 
      this.debug(message, { component: 'StorageService', data })
  };

  // Utility method to check environment
  isDev(): boolean {
    return this.isDevelopment;
  }

  // Method to temporarily enable verbose logging (useful for debugging production issues)
  enableVerboseLogging(): void {
    this.logLevel = LogLevel.DEBUG;
    this.info('Verbose logging enabled');
  }

  // Method to reset logging level to environment default
  resetLoggingLevel(): void {
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
    this.info('Logging level reset to environment default');
  }
}

export const logger = LoggingService.getInstance();
